import { useEffect, useState } from "react";
import { Cable, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KES, formatDate } from "@/lib/format";

const STATUS_LABEL = {
  pending_install: "Installation pending",
  active: "Active",
  paused: "Paused",
  suspended: "Suspended — payment overdue",
  disabled: "Disabled",
  terminated: "Terminated",
};

export default function CustomerPppoe() {
  const [accounts, setAccounts] = useState(null);
  const [payingInvoiceId, setPayingInvoiceId] = useState(null);
  const [pendingTx, setPendingTx] = useState(null); // { transactionId, invoiceId }

  const load = async () => {
    const { data } = await api.get("/customer/pppoe");
    setAccounts(data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!pendingTx) return;
    let alive = true;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/portal/transaction/${pendingTx.transactionId}`);
        if (!alive) return;
        if (data.status === "success") {
          toast.success("Payment received — thank you!");
          setPendingTx(null); setPayingInvoiceId(null);
          await load();
        } else if (data.status === "failed") {
          toast.error("Payment failed. Please try again.");
          setPendingTx(null); setPayingInvoiceId(null);
        }
      } catch { /* transient — keep polling */ }
    }, 1500);
    return () => { alive = false; clearInterval(iv); };
  }, [pendingTx]);

  const pay = async (invoiceId) => {
    setPayingInvoiceId(invoiceId);
    try {
      const { data } = await api.post(`/customer/pppoe/invoices/${invoiceId}/pay`);
      toast.success(data.message);
      setPendingTx({ transactionId: data.transaction_id, invoiceId });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setPayingInvoiceId(null);
    }
  };

  if (!accounts) return null;

  return (
    <div data-testid="customer-pppoe-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#43B02A]">// WIRED INTERNET</div>
      <h1 className="mt-1 font-heading font-black text-4xl">Your PPPoE account</h1>

      {accounts.length === 0 ? (
        <div className="mt-8 border-2 border-dashed border-[#E1E4E8] bg-white p-8 text-center">
          <Cable className="h-8 w-8 text-[#57606A] mx-auto" />
          <h2 className="font-heading font-black text-2xl mt-4">No PPPoE account yet</h2>
          <p className="text-[#57606A] mt-2">Contact us to get a wired connection installed.</p>
        </div>
      ) : (
        accounts.map((a) => {
          const unpaid = a.invoices.find((i) => i.status === "unpaid");
          const busy = payingInvoiceId === unpaid?.id;
          return (
            <div key={a.id} className="mt-8 border border-[#E1E4E8] bg-white p-8">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 grid place-items-center bg-[#43B02A]/10">
                  <Cable className="h-5 w-5 text-[#43B02A]" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#43B02A]">// {a.pppoe_username}</div>
                  <div className="font-heading font-black text-2xl">{a.plan_name}</div>
                </div>
              </div>

              {a.status === "suspended" && (
                <div className="mt-5 flex items-start gap-2 border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-3 text-sm text-[#FF3B30]">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>Your connection is suspended for non-payment. Pay the outstanding invoice below to get back online.</span>
                </div>
              )}

              <div className="mt-6 grid grid-cols-2 gap-4 text-sm font-mono">
                <div><span className="text-[#57606A]">Status</span> <span className="font-bold">{STATUS_LABEL[a.status] || a.status}</span></div>
                <div><span className="text-[#57606A]">Paid through</span> <span className="font-bold">{a.current_period_end ? formatDate(a.current_period_end) : "—"}</span></div>
                <div><span className="text-[#57606A]">↓ Download</span> <span className="font-bold">{(a.download_kbps / 1024).toFixed(1)} Mbps</span></div>
                <div><span className="text-[#57606A]">↑ Upload</span> <span className="font-bold">{(a.upload_kbps / 1024).toFixed(1)} Mbps</span></div>
              </div>

              {unpaid && (
                <div className="mt-6 pt-6 border-t border-[#E1E4E8] flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Amount due</div>
                    <div className="font-mono font-black text-2xl text-[#43B02A]">{KES(unpaid.amount_kes)}</div>
                  </div>
                  <Button onClick={() => pay(unpaid.id)} disabled={busy}
                          className="h-11 rounded-full bg-[#43B02A] hover:bg-[#368F22] text-white font-bold px-6">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "PAY NOW"}
                  </Button>
                </div>
              )}
              {busy && pendingTx?.invoiceId === unpaid?.id && (
                <p className="mt-3 text-xs font-mono text-[#57606A]">Waiting for M-Pesa confirmation…</p>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
